import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../components/HeroSection';
import { FileText, Shield, Eye, Lock, Heart, Users, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function Policies() {
  const policies = [{
    icon: <Eye className="h-8 w-8" data-id="element-1220" />,
    title: 'Accessibility Statement',
    description: 'Our commitment to making ReGrowth accessible to everyone, including our current features and ongoing work.',
    slug: 'accessibility',
    color: 'border-brand-lime',
    bgColor: 'bg-brand-lime/5',
    updated: 'January 2024'
  }, {
    icon: <Shield className="h-8 w-8" data-id="element-1221" />,
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal information. We believe in transparency and minimal data collection.',
    slug: 'privacy',
    color: 'border-brand-cyan',
    bgColor: 'bg-brand-cyan/5',
    updated: 'January 2024'
  }, {
    icon: <FileText className="h-8 w-8" data-id="element-1222" />,
    title: 'Terms of Service',
    description: 'The legal agreement between you and ReGrowth when you use our platform, courses, and content.',
    slug: 'terms',
    color: 'border-brand-orange',
    bgColor: 'bg-brand-orange/5',
    updated: 'January 2024'
  }, {
    icon: <Lock className="h-8 w-8" data-id="element-1223" />,
    title: 'Content License',
    description: 'How you can use, share, and cite content from ReGrowth. We believe in accessible knowledge.',
    slug: 'license',
    color: 'border-brand-magenta',
    bgColor: 'bg-brand-magenta/5',
    updated: 'January 2024'
  }, {
    icon: <Heart className="h-8 w-8" data-id="element-1224" />,
    title: 'Community Guidelines',
    description: 'The values and expectations that guide behavior in ReGrowth spaces, online and in courses.',
    slug: 'community',
    color: 'border-brand-lime',
    bgColor: 'bg-brand-lime/5',
    updated: 'January 2024'
  }, {
    icon: <Users className="h-8 w-8" data-id="element-1225" />,
    title: 'Anti-Harassment Policy',
    description: 'Our zero-tolerance approach to harassment, discrimination, and harm in all ReGrowth spaces.',
    slug: 'anti-harassment',
    color: 'border-brand-magenta',
    bgColor: 'bg-brand-magenta/5',
    updated: 'January 2024'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1226">
      <HeroSection title="Policies & Accessibility" subtitle="The rules, commitments, and legal stuff that govern how ReGrowth operates." tag="Transparency" bgColor="navy" data-id="element-1227" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1228">
        <div className="prose prose-lg" data-id="element-1229">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1230">
            We believe in{' '}
            <strong className="text-brand-navy" data-id="element-1231">
              transparency and accountability
            </strong>
            . These policies outline how we operate, what we expect from our
            community, and what you can expect from us. They're written in plain
            language because legal documents shouldn't require a law degree to
            understand.
          </p>
        </div>
      </section>

      {/* Policies Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1232">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1233">
          {policies.map((policy, index) => <motion.div key={policy.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} data-id="element-1234">
              <Link to={`/policies/${policy.slug}`} className={`group block border-l-4 ${policy.color} ${policy.bgColor} bg-white p-6 hover:shadow-xl transition-all h-full`} data-id="element-1235">
                <div className="flex items-start justify-between mb-4" data-id="element-1236">
                  <div className="text-brand-navy" data-id="element-1237">{policy.icon}</div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-navy group-hover:translate-x-1 transition-all" data-id="element-1238" />
                </div>

                <h3 className="text-xl font-black uppercase mb-3 text-brand-black group-hover:text-brand-navy transition-colors" data-id="element-1239">
                  {policy.title}
                </h3>

                <p className="text-gray-700 text-sm leading-relaxed mb-4" data-id="element-1240">
                  {policy.description}
                </p>

                <div className="text-xs text-gray-500 pt-4 border-t border-gray-100" data-id="element-1241">
                  Last updated: {policy.updated}
                </div>
              </Link>
            </motion.div>)}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white" data-id="element-1242">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1243">
          <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-1244">
            Quick Reference
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1245">
            <div className="border-2 border-brand-black p-6" data-id="element-1246">
              <h3 className="font-bold text-lg mb-3" data-id="element-1247">
                Questions About Policies?
              </h3>
              <p className="text-gray-700 text-sm mb-4" data-id="element-1248">
                If you have questions about any of our policies or need
                clarification, we're here to help.
              </p>
              <a href="mailto:policies@regrowth.com" className="text-brand-navy font-bold text-sm hover:underline" data-id="element-1249">
                policies@regrowth.com
              </a>
            </div>

            <div className="border-2 border-brand-black p-6" data-id="element-1250">
              <h3 className="font-bold text-lg mb-3" data-id="element-1251">Report a Violation</h3>
              <p className="text-gray-700 text-sm mb-4" data-id="element-1252">
                If you've experienced or witnessed a violation of our policies,
                please report it immediately.
              </p>
              <a href="mailto:report@regrowth.com" className="text-brand-navy font-bold text-sm hover:underline" data-id="element-1253">
                report@regrowth.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Updates */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1254">
        <div className="bg-brand-navy/5 border-l-4 border-brand-navy p-8" data-id="element-1255">
          <h3 className="text-xl font-black uppercase mb-4 text-brand-black" data-id="element-1256">
            How We Update Policies
          </h3>
          <div className="space-y-3 text-gray-700" data-id="element-1257">
            <p data-id="element-1258">
              <strong data-id="element-1259">Minor updates</strong> (typos, clarifications, formatting)
              are made as needed without notification.
            </p>
            <p data-id="element-1260">
              <strong data-id="element-1261">Significant changes</strong> (new policies, major
              revisions) are announced via email to all users at least 30 days
              before taking effect.
            </p>
            <p data-id="element-1262">
              <strong data-id="element-1263">Version history</strong> is maintained for all policies.
              You can request previous versions by emailing us.
            </p>
          </div>
        </div>
      </section>
    </div>;
}